"use client";

import React, { useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { ListingTypeEnum } from "@/types/types";
import {
  MdOutlineSync,
  MdArrowForward,
  MdKeyboardArrowUp,
  MdKeyboardArrowDown,
} from "react-icons/md";
import styles from "./floating-bar.module.css";
import { makeLocalizedHrefNoLocale } from "@/lib/localizedRoutes/routeHelpers";
import Link from "next/link";
import { toast } from "react-toastify";
import { usePathname, useRouter } from "next/navigation";
import { useAppLocale } from "@/hooks/useAppLocale";
import { newListingSubRoutes } from "@/lib/localizedRoutes/localizedRoutes";
import { useListingContext } from "../listingContex";
import { getDraftValidationSchema } from "../schema-validation";

const planPrices: Record<string, number> = {
  essential: 4.5,
  plus: 9.5,
  prime: 19.5,
  spotlight: 39.5,
};

export default function FloatingBottomBar() {
  const t = useTranslations("NewListing");
  const {
    data,
    updateField,
    updateMultipleFields,
    isSaving,
    saveDraft,
    setValidationErrors,
    getRedirectPath,
  } = useListingContext();
  const locale = useAppLocale();
  const router = useRouter();
  const subRoute = newListingSubRoutes.publish[locale];
  // Toggle for mobile view
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);

  if (!data) return null;

  const activePlan = data.listing_type || ListingTypeEnum.ESSENTIAL;
  const plans = Object.values(ListingTypeEnum);

  const handleNext = async () => {
    const schema = getDraftValidationSchema(
      data.type,
      data.purpose,
      data.listing_type,
    );

    const validation = schema.safeParse(data);

    if (!validation.success) {
      // 🚀 1. Build an error map { field: "errorMessageKey" }
      const errorsMap: Record<string, string> = {};

      validation.error.errors.forEach((err) => {
        const field = err.path[0] as string;
        // Only keep the first error per field
        if (!errorsMap[field]) {
          errorsMap[field] = err.message;
        }
      });

      // 🚀 2. Inject into the Zustand store so components light up red
      setValidationErrors(errorsMap);

      // 3. Handle Global Toast & Scroll
      const firstError = validation.error.errors[0];
      const errorField = firstError.path[0];
      const element = document.getElementById(String(errorField));
      if (element) {
        // Slight offset so it isn't glued to the top of the viewport
        const y = element.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
      return;
    }

    // 4. Success! Clear errors, Save, and Redirect
    setValidationErrors(null);
    updateMultipleFields({ user_locale: locale, save_pressed: true });
    await saveDraft();
    const path = getRedirectPath(data.listing_id);
    router.push(path);
  };
  return (
    <div className={styles.floatingWrapper}>
      <div
        className={`${styles.floatingGlass} ${isMobileExpanded ? styles.expanded : ""}`}
      >
        <div
          className={styles.mobileHeader}
          onClick={() => setIsMobileExpanded(!isMobileExpanded)}
        >
          <div className={styles.mobileHeaderInfo}>
            <span className={styles.mobilePlanBadge}>
              <div className={`${styles.dot} ${styles[activePlan]}`}></div>
              {t(
                `priceEstimateCard.${activePlan.charAt(0).toUpperCase() + activePlan.slice(1)}`,
              )}
            </span>
          </div>

          <div className={styles.mobileActions}>
            {!isMobileExpanded && (
              <>
                <button className={styles.autosaveBtnMobile}>
                  <MdOutlineSync
                    size={16}
                    className={isSaving ? styles.spin : ""}
                  />
                </button>
                <button
                  className={styles.nextBtnCompact}
                  onClick={(e) => e.stopPropagation()}
                >
                  {t("next")} <MdArrowForward size={16} />
                </button>
              </>
            )}
            {isMobileExpanded ? (
              <MdKeyboardArrowDown size={22} />
            ) : (
              <MdKeyboardArrowUp size={22} />
            )}
          </div>
        </div>

        {/* MAIN BAR CONTENT (Hidden on mobile unless expanded) */}
        <div className={styles.collapsibleContent}>
          {/* LEFT: PLAN SWITCHER */}
          <div className={styles.leftSection}>
            <div className={styles.planTextWrapper}>
              <span className={styles.listingPlanText}>Listing Plan</span>
              <Link
                className={styles.detailsLink}
                href={makeLocalizedHrefNoLocale(locale, "pricing")}
                target="_blank"
                rel="noopener noreferrer"
              >
                See details
              </Link>
            </div>
            <div className={styles.planSwitcher}>
              {plans.map((plan) => (
                <button
                  key={plan}
                  className={`${styles.planBtn} ${activePlan === plan ? styles.planBtnActive : ""}`}
                  onClick={(e) => {
                    e.preventDefault();
                    updateField("listing_type", plan);
                  }}
                >
                  <div className={styles.planHeader}>
                    <div className={`${styles.dot} ${styles[plan]}`}></div>
                    <span>
                      {t(
                        `priceEstimateCard.${plan.charAt(0).toUpperCase() + plan.slice(1)}`,
                      )}
                    </span>
                  </div>
                  <div className={styles.planPrice}>
                    {planPrices[plan]} EUR/mo
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* RIGHT: TOTAL & ACTIONS */}
          <div className={styles.rightSection}>
            <div className={styles.totalPrice}>
              Total: <strong>{planPrices[activePlan]} EUR</strong> / Month
            </div>

            <div className={styles.actions}>
              <button className={styles.autosaveBtn}>
                <MdOutlineSync
                  size={18}
                  className={isSaving ? styles.spin : ""}
                />
                <span className={styles.autosaveText}>
                  {isSaving ? "Saving..." : "Autosave"}
                </span>
              </button>

              <button className={styles.nextBtn} onClick={handleNext}>
                {t("next")}
                <MdArrowForward size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
